"use client";
import React, { useState } from "react";
import { Sparkles, Upload, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

export const InteractiveSRS = () => {
    const [prompt, setPrompt] = useState("");
    const [status, setStatus] = useState<"idle" | "analyzing" | "completed">("idle");

    const handleGenerate = () => {
        if (!prompt.trim()) return;
        setStatus("analyzing");
        setTimeout(() => {
            setStatus("completed");
        }, 2500);
    };

    return (
        <div className="w-full max-w-md mx-auto bg-surface border border-border rounded-xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-background/50 backdrop-blur-sm p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg">
                        <Sparkles className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-semibold text-primary-text text-sm">SRS AI Architect</span>
                </div>
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/20 border border-red-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/20 border border-green-500/50" />
                </div>
            </div>

            {/* Content */}
            <div className="p-5 space-y-4 bg-gradient-to-b from-surface to-background">
                {status === "idle" && (
                    <>
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-secondary-text uppercase tracking-wider">
                                Describe your application
                            </label>
                            <textarea
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="e.g. A project management tool with tasks, users, and deadlines..."
                                className="w-full h-32 bg-background border border-border rounded-lg p-3 text-sm text-primary-text placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none transition-all"
                            />
                        </div>

                        <div className="flex items-center justify-between pt-2">
                            <button className="flex items-center gap-2 text-xs text-secondary-text hover:text-primary transition-colors">
                                <Upload className="w-3.5 h-3.5" />
                                <span>Upload docs/PDFs</span>
                            </button>

                            <button
                                onClick={handleGenerate}
                                disabled={!prompt.trim()}
                                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20"
                            >
                                <span>Generate Schema</span>
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </>
                )}

                {status === "analyzing" && (
                    <div className="py-12 flex flex-col items-center justify-center text-center space-y-4 animate-in fade-in zoom-in duration-300">
                        <div className="relative">
                            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full animate-pulse" />
                            <Loader2 className="w-10 h-10 text-primary animate-spin relative z-10" />
                        </div>
                        <div className="space-y-1">
                            <h4 className="text-primary-text font-medium">Analyzing Requirements</h4>
                            <p className="text-xs text-secondary-text">Parsing entities and relationships...</p>
                        </div>
                    </div>
                )}

                {status === "completed" && (
                    <div className="py-8 flex flex-col items-center justify-center text-center space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center border border-green-500/20">
                            <CheckCircle2 className="w-6 h-6 text-green-500" />
                        </div>
                        <div className="space-y-2 px-4">
                            <h4 className="text-primary-text font-medium text-lg">Analysis Complete!</h4>
                            <p className="text-sm text-secondary-text leading-relaxed">
                                The AI has successfully analyzed your task. You may now proceed to the project workspace to review and refine your generated SRS schema.
                            </p>
                        </div>
                        <button
                            onClick={() => setStatus("idle")}
                            className="mt-4 text-xs text-primary hover:underline"
                        >
                            Start over
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
